import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase'; // Ajuste o caminho se necessário
import { getXpActions, updateXpAction } from '../../lib/adminService'; // Ajuste o caminho
import { XpActionKey } from '../../lib/gamification/xpConfig'; // Ajuste o caminho
import { toast } from 'react-hot-toast'; // Usar react-hot-toast para feedback
import { Edit2, Trash2, PlusCircle, Save, XCircle, AlertTriangle } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext'; // Importar useTheme
import { Database } from '../../types/supabase';

type XpAction = Database['public']['Tables']['xp_actions']['Row'];

// Interface para os dados do formulário de edição
interface EditFormData {
  xp_value: number;
  cooldown_minutes: number;
  max_daily: number | null;
  is_active: boolean;
}

const XpActionsAdmin: React.FC = () => {
  const { theme } = useTheme(); // Usar o hook useTheme
  const [actions, setActions] = useState<XpAction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingAction, setEditingAction] = useState<XpAction | null>(null);
  const [formData, setFormData] = useState<EditFormData | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newActionData, setNewActionData] = useState<Omit<XpAction, 'id' | 'created_at' | 'updated_at'>>({
    action_key: '',
    description: '',
    xp_value: 0,
    cooldown_minutes: 0,
    max_daily: null,
    is_active: true,
  });

  const loadActions = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await getXpActions(supabase) as { data: XpAction[] | null, error: any };
      if (fetchError) throw fetchError;
      setActions(data?.sort((a, b) => (a.description || '').localeCompare(b.description || '')) || []);
    } catch (err: any) {
      setError(`Erro ao carregar ações: ${err.message}`);
      console.error("Erro ao buscar ações de XP:", err);
      toast.error("Falha ao carregar ações de XP.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadActions();
  }, [loadActions]);

  const handleEditClick = (action: XpAction) => {
    setEditingAction(action);
    setFormData({
      xp_value: action.xp_value,
      cooldown_minutes: action.cooldown_minutes ?? 0,
      max_daily: action.max_daily,
      is_active: action.is_active ?? true,
    });
    setShowCreateForm(false);
  };

  const handleCancelEdit = () => {
    setEditingAction(null);
    setFormData(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!formData) return;
    const { name, value, type, checked } = e.target;

    setFormData(prev => {
      if (!prev) return null;
      let processedValue: string | number | boolean | null = value;

    if (type === 'checkbox') {
      processedValue = checked;
      } else if (name === 'max_daily') {
        processedValue = value === '' ? null : (Number(value) >= 0 ? Number(value) : null);
      } else if (type === 'number') {
        processedValue = Number(value);
      }

      if (name === 'cooldown_minutes') {
        processedValue = Number(processedValue) ?? 0;
      }
      if (name === 'is_active' && typeof processedValue !== 'boolean'){
        processedValue = Boolean(processedValue);
      }

      return {
        ...prev,
      [name]: processedValue,
      };
    });
  };

  const handleNewActionChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
        const { checked } = e.target as HTMLInputElement;
        setNewActionData(prev => ({
            ...(prev!),
            [name]: checked,
        }));
    } else {
        setNewActionData(prev => ({
            ...(prev!),
            [name]: (name === 'max_daily' && value === '') ? null : (name === 'xp_value' || name === 'cooldown_minutes' || name === 'max_daily' ? Number(value) : value),
        }));
    }
  };

  const handleUpdateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingAction || !formData) return;

    setIsUpdating(true);
    const toastId = toast.loading('Atualizando ação...');

    try {
      const updatePayload = {
        xp_value: Number(formData.xp_value) || 0,
        cooldown_minutes: Number(formData.cooldown_minutes) >= 0 ? Number(formData.cooldown_minutes) : 0,
        max_daily: formData.max_daily === null ? null : (Number(formData.max_daily) >= 0 ? Number(formData.max_daily) : null),
        is_active: Boolean(formData.is_active),
        updated_at: new Date().toISOString(),
      };

      const { error: updateErr } = await updateXpAction(
        supabase,
        editingAction.action_key as XpActionKey,
        updatePayload
      );

      if (updateErr) throw updateErr;

      toast.success('Ação atualizada com sucesso!', { id: toastId });
      await loadActions();
      handleCancelEdit();

    } catch (err: any) {
      toast.error(`Erro ao atualizar: ${err.message}`, { id: toastId });
      console.error("Erro ao atualizar ação:", err);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newActionData.action_key || !newActionData.description) {
        toast.error("Chave da Ação e Descrição são obrigatórios.");
        return;
    }

    const { error } = await supabase.from('xp_actions').insert(newActionData);

    if (error) {
        toast.error("Erro ao criar ação: " + error.message);
    } else {
        toast.success("Ação criada com sucesso!");
        setShowCreateForm(false);
        setNewActionData({
            action_key: '',
            description: '',
            xp_value: 0,
            cooldown_minutes: 0,
            max_daily: null,
            is_active: true,
        });
        await loadActions();
    }
  };

  const handleDelete = async (actionId: string) => {
    if (window.confirm("Tem certeza que deseja deletar esta ação de XP? Esta ação não pode ser desfeita.")) {
      const { error } = await supabase.from('xp_actions').delete().eq('id', actionId);
  if (error) {
        toast.error("Erro ao deletar ação: " + error.message);
      } else {
        toast.success("Ação deletada com sucesso!");
        await loadActions();
        if (editingAction?.id === actionId) {
            handleCancelEdit();
        }
      }
    }
  };

  // Definições de classes para modo escuro
  const cardClasses = theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white shadow-md';
  const textClasses = theme === 'dark' ? 'text-gray-300' : 'text-gray-700';
  const titleClasses = theme === 'dark' ? 'text-gray-100' : 'text-gray-800';
  const inputClasses = `w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 sm:text-sm ${
    theme === 'dark'
      ? 'bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-400 focus:ring-indigo-500 focus:border-indigo-500'
      : 'border-gray-300 placeholder-gray-400 focus:ring-indigo-500 focus:border-indigo-500'
  }`;
  const labelClasses = `block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`;
  const buttonPrimaryClasses = `inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 ${
    theme === 'dark'
      ? 'bg-indigo-500 hover:bg-indigo-600 text-white focus:ring-offset-gray-800 focus:ring-indigo-400'
      : 'bg-indigo-600 hover:bg-indigo-700 text-white focus:ring-offset-gray-50 focus:ring-indigo-500'
  }`;
  const buttonSecondaryClasses = `inline-flex items-center justify-center px-4 py-2 border text-sm font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 ${
    theme === 'dark'
      ? 'bg-gray-600 hover:bg-gray-500 text-gray-200 border-gray-500 focus:ring-offset-gray-800 focus:ring-gray-400'
      : 'bg-white hover:bg-gray-50 text-gray-700 border-gray-300 focus:ring-offset-gray-50 focus:ring-indigo-500'
  }`;
  const tableHeaderClasses = theme === 'dark' ? 'bg-gray-700 text-gray-200' : 'bg-gray-50 text-gray-500';
  const tableCellClasses = theme === 'dark' ? 'text-gray-300' : 'text-gray-600';
  const tableRowHoverClasses = theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-50';
  const tableBorderClasses = theme === 'dark' ? 'divide-gray-700' : 'divide-gray-200';

  if (isLoading) return <div className={`${textClasses} p-4`}>Carregando ações de XP...</div>;
  if (error) return <div className={`p-4 rounded-md ${theme === 'dark' ? 'bg-red-800 text-red-200' : 'bg-red-100 text-red-700'}`}><AlertTriangle className="inline mr-2" />Erro: {error}</div>;

  return (
    <div className={`p-4 sm:p-6 rounded-lg ${cardClasses}`}>
      <div className="flex justify-between items-center mb-6">
        <h2 className={`text-xl font-semibold ${titleClasses}`}>Gerenciar Ações de XP</h2>
        <button onClick={() => { setShowCreateForm(true); setEditingAction(null); }} className={`${buttonPrimaryClasses} flex items-center`}>
          <PlusCircle size={18} className="mr-2" /> Criar Nova Ação
        </button>
      </div>

      {showCreateForm && (
        <form onSubmit={handleCreateSubmit} className={`mb-8 p-4 border rounded-lg ${theme === 'dark' ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}>
          <h3 className={`text-lg font-medium mb-4 ${titleClasses}`}>Nova Ação de XP</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="new_action_key" className={labelClasses}>Chave da Ação *</label>
              <input type="text" name="action_key" id="new_action_key" value={newActionData.action_key} onChange={handleNewActionChange} className={inputClasses} required />
            </div>
            <div>
              <label htmlFor="new_description" className={labelClasses}>Descrição *</label>
              <input type="text" name="description" id="new_description" value={newActionData.description} onChange={handleNewActionChange} className={inputClasses} required />
            </div>
            <div>
              <label htmlFor="new_xp_value" className={labelClasses}>Valor de XP *</label>
              <input type="number" name="xp_value" id="new_xp_value" value={newActionData.xp_value} onChange={handleNewActionChange} className={inputClasses} required />
            </div>
            <div>
              <label htmlFor="new_cooldown_minutes" className={labelClasses}>Cooldown (minutos)</label>
              <input type="number" name="cooldown_minutes" id="new_cooldown_minutes" value={newActionData.cooldown_minutes ?? 0} onChange={handleNewActionChange} className={inputClasses} />
            </div>
            <div>
              <label htmlFor="new_max_daily" className={labelClasses}>Máximo Diário (0 ou vazio para ilimitado)</label>
              <input type="number" name="max_daily" id="new_max_daily" value={newActionData.max_daily ?? ''} onChange={handleNewActionChange} className={inputClasses} placeholder="Opcional"/>
            </div>
            <div className="flex items-center mt-6 md:mt-9">
              <input type="checkbox" name="is_active" id="new_is_active" checked={newActionData.is_active ?? true} onChange={handleNewActionChange} className={`h-4 w-4 rounded ${theme === 'dark' ? 'bg-gray-600 border-gray-500 text-indigo-500 focus:ring-indigo-600' : 'border-gray-300 text-indigo-600 focus:ring-indigo-500'}`} />
              <label htmlFor="new_is_active" className={`ml-2 ${labelClasses}`}>Ativa</label>
            </div>
          </div>
          <div className="mt-6 flex justify-end space-x-3">
            <button type="button" onClick={() => setShowCreateForm(false)} className={`${buttonSecondaryClasses} flex items-center`}>
              <XCircle size={18} className="mr-2" /> Cancelar
            </button>
            <button type="submit" className={`${buttonPrimaryClasses} flex items-center`}>
              <Save size={18} className="mr-2" /> Salvar Nova Ação
            </button>
          </div>
        </form>
      )}

      {editingAction && formData && (
        <form onSubmit={handleUpdateSubmit} className={`mb-8 p-4 border rounded-lg ${theme === 'dark' ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}>
          <h3 className={`text-lg font-medium mb-4 ${titleClasses}`}>Editando: {editingAction.action_key}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="xp_value" className={labelClasses}>Valor de XP</label>
              <input type="number" name="xp_value" id="xp_value" value={formData.xp_value} onChange={handleInputChange} className={inputClasses} />
            </div>
            <div>
              <label htmlFor="cooldown_minutes" className={labelClasses}>Cooldown (minutos)</label>
              <input type="number" name="cooldown_minutes" id="cooldown_minutes" value={formData.cooldown_minutes} onChange={handleInputChange} className={inputClasses} />
            </div>
            <div>
              <label htmlFor="max_daily" className={labelClasses}>Máximo Diário (0 ou vazio para ilimitado)</label>
              <input type="number" name="max_daily" id="max_daily" value={formData.max_daily ?? ''} onChange={handleInputChange} className={inputClasses} placeholder="Opcional"/>
            </div>
            <div className="flex items-center mt-6 md:mt-9">
              <input type="checkbox" name="is_active" id="is_active" checked={formData.is_active} onChange={handleInputChange} className={`h-4 w-4 rounded ${theme === 'dark' ? 'bg-gray-600 border-gray-500 text-indigo-500 focus:ring-indigo-600' : 'border-gray-300 text-indigo-600 focus:ring-indigo-500'}`} />
              <label htmlFor="is_active" className={`ml-2 ${labelClasses}`}>Ativa</label>
            </div>
          </div>
          <div className="mt-6 flex justify-end space-x-3">
            <button type="button" onClick={handleCancelEdit} className={`${buttonSecondaryClasses} flex items-center`}>
              <XCircle size={18} className="mr-2" /> Cancelar
            </button>
            <button type="submit" className={`${buttonPrimaryClasses} flex items-center`}>
              <Save size={18} className="mr-2" /> Salvar Alterações
            </button>
          </div>
        </form>
      )}

      <div className="overflow-x-auto">
        <table className={`min-w-full divide-y ${tableBorderClasses}`}>
          <thead className={tableHeaderClasses}>
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Chave da Ação</th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Descrição</th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">XP</th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Cooldown (min)</th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Max Diário</th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Ativa</th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Ações</th>
            </tr>
          </thead>
          <tbody className={`divide-y ${tableBorderClasses} ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
            {actions.map((action) => (
              <tr key={action.id} className={`${tableRowHoverClasses} ${editingAction?.id === action.id ? (theme === 'dark' ? 'bg-gray-750' : 'bg-indigo-50') : ''}`}>
                <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${action.action_key === editingAction?.action_key ? titleClasses : tableCellClasses}`}>{action.action_key}</td>
                <td className={`px-6 py-4 whitespace-nowrap text-sm ${tableCellClasses}`}>{action.description}</td>
                <td className={`px-6 py-4 whitespace-nowrap text-sm ${tableCellClasses}`}>{action.xp_value}</td>
                <td className={`px-6 py-4 whitespace-nowrap text-sm ${tableCellClasses}`}>{action.cooldown_minutes}</td>
                <td className={`px-6 py-4 whitespace-nowrap text-sm ${tableCellClasses}`}>{action.max_daily ?? '-'}</td>
                <td className={`px-6 py-4 whitespace-nowrap text-sm ${tableCellClasses}`}>
                  {action.is_active ? (
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${theme === 'dark' ? 'bg-green-700 text-green-200' : 'bg-green-100 text-green-800'}`}>Sim</span>
                  ) : (
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${theme === 'dark' ? 'bg-red-700 text-red-200' : 'bg-red-100 text-red-800'}`}>Não</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                  <button onClick={() => handleEditClick(action)} className={`${buttonSecondaryClasses} px-3 py-1 text-xs flex items-center`}>
                    <Edit2 size={14} className="mr-1" /> Editar
                  </button>
                  <button onClick={() => handleDelete(action.id)} className={`inline-flex items-center justify-center px-3 py-1 border border-transparent text-xs font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 ${theme === 'dark' ? 'bg-red-700 hover:bg-red-800 text-red-100 focus:ring-offset-gray-800 focus:ring-red-600' : 'bg-red-600 hover:bg-red-700 text-white focus:ring-offset-gray-50 focus:ring-red-500'} flex items-center`}>
                    <Trash2 size={14} className="mr-1" /> Deletar
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {actions.length === 0 && !isLoading && (
        <p className={`${textClasses} mt-4 text-center`}>Nenhuma ação de XP encontrada.</p>
      )}
    </div>
  );
};

export default XpActionsAdmin; 